/**
 * Minimal JSON-schema validator for the served agent-config v2 schema
 * (`GET /api/agent-config-schema`, schemars-generated). The schema's
 * constructs are a small, stable subset — type checks (incl. `anyOf`
 * nullability), enums, `properties` + `required` +
 * `additionalProperties: false`, `minProperties`/`maxProperties`, array
 * `items`, and local `$defs` refs —
 * so a purpose-built ~100-line walker covers it exactly, with the
 * redesign's error phrasing baked in. The companion test pins the walker
 * against the constructs the served schema actually uses; if the schema
 * ever grows a construct outside this subset, that test fails loudly
 * rather than silently accepting configs.
 */

interface Schema {
  type?: string | string[]
  enum?: unknown[]
  properties?: Record<string, Schema>
  required?: string[]
  additionalProperties?: boolean
  minProperties?: number
  maxProperties?: number
  items?: Schema
  anyOf?: Schema[]
  $ref?: string
  $defs?: Record<string, Schema>
  [k: string]: unknown
}

interface SchemaIssue {
  /** Dotted path from the config root ("stop.max_iterations"). */
  path: string
  message: string
}

function resolve(root: Schema, schema: Schema): Schema {
  if (schema.$ref) {
    const name = schema.$ref.split('/').pop() as string
    const target = root.$defs?.[name]
    return target ? resolve(root, target) : schema
  }
  return schema
}

function typeOf(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  if (typeof value === 'number')
    return Number.isInteger(value) ? 'integer' : 'number'
  return typeof value
}

function typeMatches(value: unknown, expected: string): boolean {
  const actual = typeOf(value)
  if (expected === 'number') return actual === 'number' || actual === 'integer'
  if (expected === 'integer') return actual === 'integer'
  return actual === expected
}

function walk(
  root: Schema,
  schema: Schema,
  value: unknown,
  path: string,
  issues: SchemaIssue[],
): void {
  const s = resolve(root, schema)

  if (s.anyOf) {
    const ok = s.anyOf.some((alt) => {
      const probe: SchemaIssue[] = []
      walk(root, alt, value, path, probe)
      return probe.length === 0
    })
    if (!ok && s.anyOf.length > 0) {
      // Distinguish the null arm for friendlier messages.
      const nonNull = s.anyOf.filter((alt) => {
        const r = resolve(root, alt)
        return !(r.type === 'null')
      })
      if (nonNull.length === 1) {
        walk(root, nonNull[0], value, path, issues)
        return
      }
      issues.push({
        path,
        message: `must match one of the allowed shapes (${path})`,
      })
    }
    return
  }

  if (s.type) {
    const expected = Array.isArray(s.type) ? s.type : [s.type]
    if (!expected.some((t) => typeMatches(value, t))) {
      issues.push({
        path,
        message: `expected ${expected.join(' | ')}, got ${typeOf(value)}${
          path ? ` (${path})` : ''
        }`,
      })
      return
    }
  }

  if (
    s.enum &&
    !s.enum.some((v) => JSON.stringify(v) === JSON.stringify(value))
  ) {
    issues.push({
      path,
      message: `${JSON.stringify(value)} is not one of ${s.enum
        .map((v) => JSON.stringify(v))
        .join(', ')}${path ? ` (${path})` : ''}`,
    })
  }

  if (typeOf(value) === 'object') {
    // Only `reasoning` bounds its keys: exactly one control and its value.
    const count = Object.keys(value as object).length
    if (
      (s.minProperties !== undefined && count < s.minProperties) ||
      (s.maxProperties !== undefined && count > s.maxProperties)
    ) {
      issues.push({
        path,
        message: `${path} must be one control and its value`,
      })
    }
  }

  if (typeOf(value) === 'object' && s.properties) {
    const obj = value as Record<string, unknown>
    for (const key of s.required ?? []) {
      if (!(key in obj)) {
        issues.push({ path, message: `missing required key "${key}"` })
      }
    }
    for (const key of Object.keys(obj)) {
      const child = s.properties[key]
      if (!child) {
        if (s.additionalProperties === false) {
          issues.push({
            path: path ? `${path}.${key}` : key,
            message: `unknown key "${key}" — not in agent-config v2`,
          })
        }
        continue
      }
      walk(root, child, obj[key], path ? `${path}.${key}` : key, issues)
    }
  }

  if (typeOf(value) === 'array' && s.items) {
    ;(value as unknown[]).forEach((item, i) => {
      walk(root, s.items as Schema, item, `${path}[${i}]`, issues)
    })
  }
}

export type ValidationStatus =
  | { kind: 'empty' }
  | { kind: 'invalid'; issues: SchemaIssue[] }
  | { kind: 'valid'; warnings: SchemaIssue[] }

/**
 * Validate a parsed config object against the served schema. Parse
 * errors are the caller's concern; this walks a parsed value.
 * Warnings (currently: missing model, which falls back to the provider
 * default) do not block saving.
 */
export function validateConfig(
  schema: unknown,
  config: unknown,
): ValidationStatus {
  const root = schema as Schema
  const issues: SchemaIssue[] = []
  walk(root, root, config, '', issues)
  if (issues.length > 0) return { kind: 'invalid', issues }
  const warnings: SchemaIssue[] = []
  const obj = (config ?? {}) as Record<string, unknown>
  if (!obj.model) {
    warnings.push({
      path: 'model',
      message: 'model not set — falls back to the provider default',
    })
  }
  return { kind: 'valid', warnings }
}
